import {
  Badge, Card, CardHeader, Spinner, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { BotRegular, DataBarHorizontalRegular, TargetArrowRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useConversionAccuracy, useConversionReview } from '../../services/complianceApi';

const pct = (x: number) => `${Math.round(x * 100)}%`;
const money = (n?: number | null) => (n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
// The figure table has always reddened below 90% exact; the monthly bars follow the same line, so a 29% month is never green.
const GOOD = 0.9;

// Polish pass (2026-10-28): this tab was the one surface on hand-rolled tables, inline pixel sizes and stored codes
// (sales_tax, grossSales, 2026-07). It now uses the Fluent table, the type ramp and the words staff read elsewhere.
const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  headline: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: tokens.spacingHorizontalXXXL },
  stat: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  statValue: { fontSize: tokens.fontSizeHero800, lineHeight: tokens.lineHeightHero800, fontWeight: tokens.fontWeightSemibold },
  statLabel: { color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase300 },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
  sectionTitle: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400,
  },
  trend: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  trendRow: {
    display: 'grid', gridTemplateColumns: '9rem 1fr 3rem', alignItems: 'center',
    gap: tokens.spacingHorizontalM, fontSize: tokens.fontSizeBase200,
  },
  track: { height: '8px', borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground3 },
  barGood: { height: '8px', borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorPaletteGreenBackground3 },
  barLow: { height: '8px', borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorPaletteRedBackground3 },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  low: { color: tokens.colorPaletteRedForeground1 },
  padded: { padding: tokens.spacingVerticalM },
  scroll: { overflowX: 'auto' },
});

export default function ConversionTab() {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const { data: acc, isLoading, isError } = useConversionAccuracy();
  const { data: review, isLoading: reviewLoading } = useConversionReview();

  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const monthLabel = (m: string) =>
    /^\d{4}-\d{2}$/.test(m)
      ? new Date(`${m}-01T00:00:00Z`).toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
      : m;
  const typeLabel = (code: string) => t(`managementPage.conversion.obligationTypes.${code}`, { defaultValue: code });
  const figureLabel = (code: string) => t(`amounts.${code}`, { defaultValue: code });

  return (
    <div className={styles.root}>
      {/* Exact-match metrics. Loading/error/empty are scoped to this section so the
          review log below always renders — a reconciled pair without totalDue shows
          in the review even when the headline pair count is zero. */}
      {isLoading ? (
        <div className={styles.padded}><Spinner size="small" /></div>
      ) : isError || !acc ? (
        <div className={styles.padded}>{t('managementPage.conversion.loadError')}</div>
      ) : acc.overall.count === 0 ? (
        <div className={styles.padded}>{t('managementPage.conversion.empty')}</div>
      ) : (
        <>
          <div className={styles.headline}>
            <div className={styles.stat}>
              <span data-testid="exact-totaldue-pct" className={styles.statValue}>{pct(acc.overall.totalDueExactPct)}</span>
              <span className={styles.statLabel}>{t('managementPage.conversion.totalDueExact')}</span>
            </div>
            <div className={styles.stat}>
              <span data-testid="exact-allfigures-pct" className={styles.statValue}>{pct(acc.overall.allFiguresExactPct)}</span>
              <span className={styles.statLabel}>{t('managementPage.conversion.allFiguresExact')}</span>
            </div>
            <span className={styles.muted}>{t('managementPage.conversion.reconciledPairs', { count: acc.overall.count })}</span>
          </div>

          {acc.byMonth.length > 0 && (
            <Card>
              <CardHeader header={<div className={styles.sectionTitle}><TargetArrowRegular /><span>{t('managementPage.conversion.byMonth.title')}</span></div>} />
              <div className={styles.trend}>
                {acc.byMonth.slice(-6).map((m) => (
                  <div key={m.month} className={styles.trendRow} data-testid="conversion-month">
                    <span>{monthLabel(m.month)}</span>
                    <div className={styles.track}>
                      <div
                        className={m.totalDueExactPct >= GOOD ? styles.barGood : styles.barLow}
                        style={{ width: pct(m.totalDueExactPct) }}
                      />
                    </div>
                    <span className={styles.num}>{pct(m.totalDueExactPct)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card>
            <CardHeader header={<div className={styles.sectionTitle}><DataBarHorizontalRegular /><span>{t('managementPage.conversion.byType.title')}</span></div>} />
            <Table size="small" aria-label={t('managementPage.conversion.byType.title')}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('managementPage.conversion.byType.columns.type')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.conversion.byType.columns.pairs')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.conversion.byType.columns.totalDueExact')}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {acc.byObligationType.map((o) => (
                  <TableRow key={o.obligationType}>
                    <TableCell>{typeLabel(o.obligationType)}</TableCell>
                    <TableCell className={styles.num}>{o.count}</TableCell>
                    <TableCell className={o.totalDueExactPct < GOOD ? `${styles.num} ${styles.low}` : styles.num}>{pct(o.totalDueExactPct)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* By figure — worst first (what to fix) */}
          <Card>
            <CardHeader header={<div className={styles.sectionTitle}><DataBarHorizontalRegular /><span>{t('managementPage.conversion.byFigure.title')}</span></div>} />
            <Table size="small" aria-label={t('managementPage.conversion.byFigure.title')}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('managementPage.conversion.byFigure.columns.figure')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.conversion.byFigure.columns.exact')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.conversion.byFigure.columns.meanError')}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {acc.byFigure.map((f) => (
                  <TableRow key={f.figure}>
                    <TableCell title={f.figure}>{figureLabel(f.figure)}</TableCell>
                    <TableCell className={f.exactPct < GOOD ? `${styles.num} ${styles.low}` : styles.num}>{pct(f.exactPct)}</TableCell>
                    <TableCell className={styles.num}>{money(f.meanAbsError)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* Conversion review log (moved from the dashboard). Independent of the metrics
          section — renders its own loading/empty states. */}
      <Card data-testid="conversion-review-card">
        <CardHeader
          header={
            <div className={styles.sectionTitle}>
              <BotRegular />
              <span>{t('managementPage.conversion.review.title')}</span>
              {review && review.length > 0 && <Badge appearance="tint" color="informative">{review.length}</Badge>}
            </div>
          }
          description={<Text className={styles.muted}>{t('managementPage.conversion.review.description')}</Text>}
        />
        {reviewLoading ? (
          <div className={styles.padded}><Spinner size="small" /></div>
        ) : !review || review.length === 0 ? (
          <div className={styles.padded}>{t('managementPage.conversion.review.empty')}</div>
        ) : (
          <div className={styles.scroll}>
            <Table size="small" aria-label={t('managementPage.conversion.review.title')}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('managementPage.conversion.review.columns.client')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.conversion.review.columns.obligation')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.conversion.review.columns.period')}</TableHeaderCell>
                  <TableHeaderCell className={styles.num}>{t('managementPage.conversion.review.columns.computed')}</TableHeaderCell>
                  <TableHeaderCell className={styles.num}>{t('managementPage.conversion.review.columns.filed')}</TableHeaderCell>
                  <TableHeaderCell className={styles.num}>{t('managementPage.conversion.review.columns.difference')}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {review.slice(0, 20).map((r) => (
                  <TableRow key={`${r.taskId}-${r.obligationId}`}>
                    <TableCell>{r.clientName ?? '—'}</TableCell>
                    <TableCell>{r.obligationName ?? (r.obligationType ? typeLabel(r.obligationType) : '—')}</TableCell>
                    <TableCell>{r.periodLabel ?? '—'}</TableCell>
                    <TableCell className={styles.num}>{money(r.computed.totalDue)}</TableCell>
                    <TableCell className={styles.num}>{money(r.filed.totalDue)}</TableCell>
                    <TableCell className={r.totalDueVariance > 1 ? `${styles.num} ${styles.low}` : styles.num}>{money(r.variance.totalDue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
